import { ChangeEvent } from "react";

import { Client } from "@/Common/Api/Client";
import { IndividualApiClient } from "@/Common/Api/Individuals/IndividualApiClient";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { FindPatientDuplicateRequest } from "@/Common/Api/Patient/Dto/FindPatientDuplicateRequest";
import { HealthInsuranceEligibilityClient } from "@/Common/Api/Patient/HealthInsuranceEligibilityClient";
import { ValidationRule } from "@/Common/Api/Validation/Dto/Responses/ValidationRule";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { AddressControl, AddressControlDTO } from "@/Common/Components/Contacts/AddressControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { RadioButton } from "@/Common/Components/Inputs/RadioButton";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { HealthCardComponent } from "@/Common/Components/Patient/HealthCardComponents/HealthCardComponent";
import { HealthCardValidatorFactory, HealthCardValidatorRequest } from "@/Common/Components/Patient/HealthCardComponents/HealthCardValidator";
import { IPatientSelectCallbackFunction } from "@/Common/Components/Search/PatientAdvancedSearchControl";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { IndividualIdentityHelper } from "@/Common/Helpers/IndividualIdentityHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { Address } from "@/Common/Models/Contacts/Address";
import { PhoneType } from "@/Common/Models/Contacts/Enums/PhoneType";
import { ContactFacetType } from "@/Common/Models/Individuals/Enums/ContactFacetType";
import { GenderIdentity } from "@/Common/Models/Individuals/Enums/GenderIdentity";
import { PreferredPronouns } from "@/Common/Models/Individuals/Enums/PreferredPronouns";
import { IJPatientCreationModel, IPatientCreationModel } from "@/Common/Models/Patient/UI/IPatientCreationModel";
import { Person } from "@/Common/Models/Person/Person";
import { RAMQInfo } from "@/Common/Models/RAMQ/RAMQInfo";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
// eslint-disable-next-line import/no-restricted-paths
import { IndividualAdvancedSearchControl } from "../Search/IndividualAdvancedSearchControl";

import img_loading from "@/Globals/_images/loading.gif";

interface IPatientViewControlProps {
    patientModel: IPatientCreationModel;
    cancelOperation?: number;
    saveOperation?: number;
    id?: string;
    cssClass?: string;
    stateUpdateFunction?: (state: Readonly<PatientEditState>) => void; // state notifier function when component is used as sub-component
    hideFooter?: boolean; // used to hide the cancel/save buttons in case component is used as sub-component
    disabled?: boolean; // used to disable all controls

    // these are used to deal with the duplicate patient "popup" actions that can differ from where this component is used (ie sidebar=> navigate to patient file, calendar => associate patient with appt)
    duplicateContinueOperation?: number;
    duplicateContinueCallback?: IPatientSelectCallbackFunction;
    duplicatePromptLabelName: string;
    duplicatePopupCSS?: string; // css class name to adjust duplicate popup if needed
    shouldNotValidateDuplicate?: boolean;

    /**
     * When a duplicate that is not a patient is found, display a button to convert it.
     */
    enableQuickPatientConversionForDuplicates?: boolean;

    onValidHealthCardInfo?: (patientCreationModel: IPatientCreationModel) => void;
}

class PatientEditState {
    public isLoadingRAMQ: boolean;

    public patientModel: IPatientCreationModel;
    public createInProgress: boolean;
    public phoneTypeLookups: [FlatLookupView, FlatLookupView][];
    public emailTypelookups: FlatLookupView[];
    public duplicatePerson: Person;
    public isCheckingEligibility: boolean;

    constructor(init?: Partial<PatientEditState>) {
        Object.assign(this, init);
    }

    public static IsValid(s: PatientEditState): boolean {
        return (
            !!s.patientModel.get("FirstName")
            && !!s.patientModel.get("LastName")
            && s.patientModel.get("IsNAMValid")
            && s.patientModel.get("IsPhone1Valid")
            && s.patientModel.get("IsPhone2Valid")
            && s.patientModel.get("IsPostalCodeValid")
            && !s.patientModel.get("DuplicatePopupOpen")
            && s.patientModel.get("IsEmailValid")
            && (!cacheStore.ClinicSettings.EnableNameFamilyDDNMandatoryPatientCreation || !!s.patientModel.get("DOB"))
            && (!cacheStore.ClinicSettings.EnableMotherNameMandatoryPatientCreation || !!s.patientModel.get("motherFirstName"))
            && (!cacheStore.ClinicSettings.EnableMotherNameMandatoryPatientCreation || !!s.patientModel.get("motherLastName"))
        );
    }
}

class PatientEdit extends BaseReactComponent<IPatientViewControlProps, PatientEditState> {
    private id: string;
    private ontarioEligibilityCheckTimeoutId: ReturnType<typeof setTimeout>;
    private readonly validationHelper = new ValidationHelper();

    // get default gender radio button options
    private readonly maleLookupId = cacheStore.FindLookupByLookupCode("MALE");
    private readonly malePrefixLookupId = cacheStore.FindLookupByLookupCode("PREFIX.MR");
    private readonly femaleLookupId = cacheStore.FindLookupByLookupCode("FEMALE");
    private readonly femalePrefixLookupId = cacheStore.FindLookupByLookupCode("PREFIX.MS");
    private readonly mexicoCountryLookup = cacheStore.FindLookupByLookupCode("CNMEXICO");

    private alreadyResolvedNam = "";
    private alreadyDuplicateCheckedNam: FindPatientDuplicateRequest;
    private readonly homePhoneLookupId = cacheStore.getLookupByPhoneType(PhoneType.Phone).LookupID;
    private readonly cellPhoneLookupId = cacheStore.getLookupByPhoneType(PhoneType.Mobile).LookupID;

    public constructor(props: IPatientViewControlProps) {
        super(props);
        this.id = props.id ?? sessionStore.newId().toString();

        const initialState = new PatientEditState();
        initialState.patientModel = props.patientModel;
        initialState.patientModel = initialState.patientModel.set("SelectedCountryLookupId", cacheStore.GetDefaultCountryForActiveSite().LookupID);

        initialState.phoneTypeLookups = cacheStore.GetPhoneContactPointTypeLookupTuples();
        initialState.emailTypelookups = cacheStore.GetEmailContactPointTypeLookups();

        if (initialState.patientModel.get("EmailEdit") == null || initialState.patientModel.get("EmailEdit").length == 0) {
            if (props.patientModel.get("Emails") != null && props.patientModel.get("Emails").size > 0) {
                const initialEmail = props.patientModel.get("Emails").toArray()[0];
                initialState.patientModel = initialState.patientModel.set("EmailEdit", initialEmail.Address);
            }
        }

        initialState.createInProgress = false;
        initialState.isCheckingEligibility = false;
        this.state = initialState;
    }

    public componentDidMount(): void {
        if (this.props.stateUpdateFunction != null) {
            this.props.stateUpdateFunction(this.state);
        }
    }

    public componentDidUpdate(prevProps: IPatientViewControlProps, prevState: PatientEditState): void {
        if (this.props.patientModel !== prevProps.patientModel) {
            const newState = new PatientEditState();
            newState.patientModel = this.props.patientModel;

            const healthCardInfo = new FindPatientDuplicateRequest({
                number: this.state.patientModel.get("NAMEdit"),
                regionLookupId: IJPatientCreationModel.getHealthCardRegionLookupId(this.state.patientModel)
            });

            this.setState(newState, () => !this.props.shouldNotValidateDuplicate && this.validateIsNotDuplicate(healthCardInfo));
            return; // Prevents duplicate stateUpdateFunction call below
        }

        if (this.props.stateUpdateFunction != null && this.state.patientModel != prevState.patientModel) {
            this.props.stateUpdateFunction(this.state);
        }
    }

    public shouldComponentUpdate(nextProps: IPatientViewControlProps, nextState: PatientEditState): boolean {
        return (
            nextState.patientModel != this.state.patientModel
            || nextState.createInProgress != this.state.createInProgress
            || nextState.isLoadingRAMQ != this.state.isLoadingRAMQ
            || nextState.duplicatePerson != this.state.duplicatePerson
            || nextState.isCheckingEligibility != this.state.isCheckingEligibility
        );
    }

    public render(): JSX.Element {
        const isSelectedProvinceOntario = this.state.patientModel.get("SelectedRegionLookupId") == cacheStore.FindLookupByLookupCode("CanadaState_ON").LookupID;
        const isMexicoCURP = this.state.patientModel.get("SelectedCountryLookupId") == this.mexicoCountryLookup.LookupID;

        // fetch phoneNUmber mask info
        let phonevalidationRule = this.validationHelper.getPhoneNumberValidationRule(this.state.patientModel.get("SelectedCountryLookupId"), null); // no need for region when checking for phone number format?
        let phonemask: string = phonevalidationRule ? phonevalidationRule.EditMaskPattern : "";
        let phonevalidationErrorMessage: string = (phonevalidationRule && phonevalidationRule.ValidationErrorMessageLookupId) ? cacheStore.GetLocalizedValueByLookupId(phonevalidationRule.ValidationErrorMessageLookupId) : "";

        // fetch nam mask info
        let hivalidationRule = this.validationHelper.getHealthcardValidationRule(this.state.patientModel.get("SelectedCountryLookupId"), !isMexicoCURP ? this.state.patientModel.get("SelectedRegionLookupId") : null);
        let hivalidationErrorMessage: string = cacheStore.GetString("InvalidLabel");// (hivalidationRule && hivalidationRule.ValidationErrorMessageLookupId) ? cacheStore.GetLocalizedValueByLookupId(hivalidationRule.ValidationErrorMessageLookupId) : "";
        let healthIdentificationMask = hivalidationRule?.EditMaskPattern;

        // fetch email validation info
        let validationRule = this.validationHelper.getEmailValidationRule();
        let EmailMask: string = validationRule ? validationRule.EditMaskPattern : "";

        // check form validity
        let notEmptyValidationRule = new ValidationRule();
        notEmptyValidationRule.ValidationRegexPattern = "([^\\s]*)";

        let isDuplicatePopupOpen = this.state.patientModel.get("DuplicatePopupOpen");

        let phone1TypeLookupId: string = this.state.patientModel.get("Phone1TypeLookupId");
        let phone1ContactLookupId: string = this.state.patientModel.get("Phone1ContactTypeLookupId");
        let phone1value = phone1ContactLookupId + "_" + phone1TypeLookupId;
        let phone2TypeLookupId: string = this.state.patientModel.get("Phone2TypeLookupId");
        let phone2ContactLookupId: string = this.state.patientModel.get("Phone2ContactTypeLookupId");
        let phone2value = phone2ContactLookupId + "_" + phone2TypeLookupId;
        let showPhone1Ext: boolean = phone1TypeLookupId == this.homePhoneLookupId;
        let showPhone2Ext: boolean = phone2TypeLookupId == this.homePhoneLookupId;
        let showPhone1Notification = phone1TypeLookupId === this.cellPhoneLookupId;
        let showPhone2Notification = phone2TypeLookupId === this.cellPhoneLookupId;

        let emailTypeValue: string = this.state.patientModel.get("EmailTypeByLookup");

        let initialStreetAddress: string = this.state.patientModel.get("CivicAddressEdit");
        let initialStateLookupId: string = this.state.patientModel.get("SelectedRegionLookupId") || cacheStore.GetDefaultRegionForActiveSite().LookupID;
        let initialCountryLookupId: string = this.state.patientModel.get("SelectedCountryLookupId") || cacheStore.GetDefaultCountryForActiveSite().LookupID;
        let initialCity: string = this.state.patientModel.get("CityEdit");
        let initialPostalCode: string = this.state.patientModel.get("PostalCodeEdit");
        let initialAddresses: Address[] = this.state.patientModel.get("Addresses").toArray();
        if (initialAddresses != null && initialAddresses.length > 0) {
            if (!initialCity)
                initialCity = initialAddresses[0].City;

            if (!initialPostalCode)
                initialPostalCode = initialAddresses[0].ZipCode;
        }

        let validatorCountryLookupId = cacheStore.ClinicSettings.EnableHealthCardValidation
            ? cacheStore.GetDefaultCountryForActiveSite().LookupID
            : initialCountryLookupId;

        const healthCardValidator = HealthCardValidatorFactory.getValidator(validatorCountryLookupId, IJPatientCreationModel.getHealthCardRegionLookupId(this.state.patientModel));

        return (
            <div className={"patient-edit " + this.props.cssClass}>

                {isDuplicatePopupOpen &&
                    // render an overlay to block user input when displaying the duplicate file "popup"
                    <div className="fixed-overlay"> </div>
                }

                <div className="content">
                    {isDuplicatePopupOpen &&
                        this.renderDuplicatePopup()
                    }

                    {this.renderRow(
                        healthCardValidator.healthCardLabel,
                        <div className="flex-container">
                            <div style={{ width: "100%" }}>
                                {cacheStore.ClinicSettings.EnableHealthCardValidation
                                    ? (
                                        <HealthCardComponent
                                            healthCard={this.state.patientModel.get("NAMEdit") ?? ""}
                                            expiryDate={this.state.patientModel.get("NAMExpiryEdit")}
                                            regionLookupId={this.state.patientModel.get("selectedHealthCardRegionLookupId")}
                                            countryLookupId={cacheStore.GetDefaultCountryForActiveSite().LookupID}
                                            isValid={this.state.patientModel.get("IsNAMValid")}
                                            onHealthCardNumberChanged={healthCard => this.onNAMChange(healthCard)}
                                            onExpiryDateChanged={expiryDate => this.onNAMExpiryChange(expiryDate)}
                                            onRegionLookupIdChanged={regionLookupId => this.handleOnHealthCardRegionChange(regionLookupId)}
                                        />
                                    )
                                    : (
                                        <TextboxMaskedControl
                                            isValid={this.state.patientModel.get("IsNAMValid")}
                                            focus
                                            id={"namedit" + this.id}
                                            value={this.state.patientModel.get("NAMEdit") ?? ""}
                                            onChangeFunction={value => this.onNAMChange(value)}
                                            mask={healthIdentificationMask}
                                            validationErrorMessage={hivalidationErrorMessage}
                                            returnMaskedValue={isSelectedProvinceOntario}
                                            disabled={this.state.isCheckingEligibility}
                                        />
                                    )
                                }

                            </div>

                            {!isMexicoCURP && !cacheStore.ClinicSettings.EnableHealthCardValidation && (
                                <div style={{ paddingLeft: "5px" }}>
                                    <DateSelector
                                        id={"NAMExpiry_Date" + this.props.id}
                                        width={130}
                                        cssClass="rounded"
                                        date={this.state.patientModel.get("NAMExpiryEdit")}
                                        onChangeCallback={date => this.onNAMExpiryChange(date)}
                                        dateFormat="yyyy MMMM"
                                        disabled={this.state.isCheckingEligibility}
                                        isMonthPicker
                                    />
                                </div>
                            )}
                        </div>,
                        false,
                        this.state.isLoadingRAMQ
                    )}

                    {this.renderRow(
                        cacheStore.GetString("PrefixLabel"),
                        <select
                            onChange={this.onPrefixChanged.bind(this)}
                            className="form-control myle-control"
                            data-cy="patientEdit-prefix-select"
                            value={this.state.patientModel.get("SelectedPrefixLookupId")}
                            disabled={this.state.isCheckingEligibility}
                        >
                            <option key="sdsds" value="">  </option>
                            {this.state.patientModel.get("AvailablePrefixLookups").map(prefix => (
                                <option key={prefix.LookupID} value={prefix.LookupID}>{cacheStore.GetLocalizedValueByLookupId(prefix.LookupID)}</option>
                            ))}
                        </select>
                    )}

                    {this.renderRow(
                        cacheStore.GetString("LastNameLabel"),
                        <TextboxControl
                            className="form-control"
                            dataCy="patientEdit-lastname-textbox"
                            value={this.state.patientModel.get("LastName")}
                            valid={!!this.state.patientModel.get("LastName")}
                            onChange={this.onLastNameChange.bind(this)}
                            disabled={this.state.isCheckingEligibility}
                        />,
                        true
                    )}

                    {this.renderRow(
                        cacheStore.GetString("FirstNameLabel"),
                        <TextboxControl
                            className="form-control"
                            dataCy="patientEdit-firstname-textbox"
                            value={this.state.patientModel.get("FirstName")}
                            valid={!!this.state.patientModel.get("FirstName")}
                            onChange={this.onFirstNameChange.bind(this)}
                            disabled={this.state.isCheckingEligibility}
                        />,
                        true,
                        this.state.isCheckingEligibility
                    )}

                    {this.renderRow(
                        cacheStore.GetString("PreferredNameLabel"),
                        <TextboxControl
                            className="form-control"
                            value={this.state.patientModel.get("PreferredName")}
                            valid={true}
                            onChange={this.onPreferredNameChange.bind(this)}
                            disabled={this.state.isCheckingEligibility}
                        />
                    )}

                    {this.renderRow(
                        cacheStore.GetString("DateofBirthLabel"),
                        <DateSelector
                            id={"DOB_Change" + this.id}
                            dataCy="patientEdit-DOB-selector"
                            cssClass="rounded"
                            width={140}
                            date={this.state.patientModel.get("DOB")}
                            onChangeCallback={this.onDOBChange.bind(this)}
                            onSelectOp={null}
                            dateFormat="yyyy/MM/dd"
                            valid={!cacheStore.ClinicSettings.EnableNameFamilyDDNMandatoryPatientCreation || !!this.state.patientModel.get("DOB")}
                            disabled={this.state.isCheckingEligibility}
                        />,
                        false,
                        this.state.isCheckingEligibility
                    )}

                    {this.renderRow(
                        <span className="form-label">{cacheStore.GetString("SexAtBirthLabel")}</span>,
                        <span style={{ margin: "5px -9px 0 5px", display: "inline-flex", verticalAlign: "bottom" }}>
                            <RadioButton
                                mainDivCSS=""
                                items={Immutable.List([
                                    new KeyValuePair(cacheStore.GetLocalizedValueByLookupId(this.maleLookupId.LookupID).substr(0, 1), this.maleLookupId.LookupID),
                                    new KeyValuePair(cacheStore.GetLocalizedValueByLookupId(this.femaleLookupId.LookupID).substr(0, 1), this.femaleLookupId.LookupID)
                                ])}
                                radioInputGroupId="language"
                                onePerLine={false}
                                changeOperation={null}
                                onChange={item => this.onGenderChange(item.Value)}
                                selectedItem={this.state.patientModel.get("GenderLookupId")}
                                disabled={this.state.isCheckingEligibility}
                            />
                        </span>,
                        false,
                        this.state.isCheckingEligibility
                    )}

                    {this.renderRow(
                        cacheStore.GetString("GenderIdentityLabel"),
                        <select
                            onChange={this.onGenderIdentityChange.bind(this)}
                            className="form-control myle-control"
                            value={EnumHelper.getName(GenderIdentity, this.state.patientModel.get("GenderIdentity"))}
                            disabled={this.state.isCheckingEligibility}
                        >
                            {IndividualIdentityHelper.getAllGenderIdentitiesAsHtmlOption()}
                        </select>
                    )}

                    {this.renderRow(
                        cacheStore.GetString("PreferredPronounsLabel"),
                        <select
                            onChange={value => this.onPreferredPronounsChange(value)}
                            className="form-control myle-control"
                            value={EnumHelper.getName(PreferredPronouns, this.state.patientModel.get("PreferredPronouns"))}
                            disabled={this.state.isCheckingEligibility}
                        >
                            {IndividualIdentityHelper.getAllPreferredPronounsAsHtmlOption()}
                        </select>
                    )}

                    {this.state.patientModel.get("PreferredPronouns") === PreferredPronouns.Other && (
                        this.renderRow(
                            "",
                            <TextboxControl
                                className="form-control"
                                value={this.state.patientModel.get("SpecifiedPreferredPronouns")}
                                valid={true}
                                onChange={this.onSpecifiedPreferredPronounsChange.bind(this)}
                            />
                        )
                    )}

                    {this.renderRow(
                        cacheStore.GetString("MotherLastNameLabel"),
                        <TextboxControl
                            className="form-control"
                            value={this.state.patientModel.get("motherLastName")}
                            valid={!cacheStore.ClinicSettings.EnableMotherNameMandatoryPatientCreation || !!this.state.patientModel.get("motherLastName")}
                            onChange={value => this.mutateSinglePatientModel("motherLastName", value)}
                            disabled={this.state.isCheckingEligibility}
                        />
                    )}

                    {(cacheStore.ClinicSettings.EnableMotherNameMandatoryPatientCreation || this.state.patientModel.get("motherLastName") || this.state.patientModel.get("motherFirstName")) &&
                        this.renderRow(
                            cacheStore.GetString("MotherFirstNameLabel"),
                            <TextboxControl
                                className="form-control"
                                value={this.state.patientModel.get("motherFirstName")}
                                valid={!cacheStore.ClinicSettings.EnableMotherNameMandatoryPatientCreation || !!this.state.patientModel.get("motherFirstName")}
                                onChange={value => this.mutateSinglePatientModel("motherFirstName", value)}
                                disabled={this.state.isCheckingEligibility}
                            />
                        )
                    }

                    <div style={{ height: "14px" }} />

                    <AddressControl
                        id={this.id}
                        initialStreetAddress={initialStreetAddress}
                        initialCity={initialCity}
                        initialPostalCode={initialPostalCode}
                        initialCountryLookupId={initialCountryLookupId}
                        initialStateLookupId={initialStateLookupId}
                        onNotifyParentCallback={dto => this.onCivicAddressSelect(dto)}
                        rowClassName="row"
                        labelClassName="col-xs-4 text-right"
                        bodyClassName="col-xs-8"
                        isDisabled={this.state.isCheckingEligibility}

                    />

                    {/* PHONE 1 */}

                    {this.renderRow(
                        <select id="phone2Type" key="phone2Type" onChange={(e: any) => this.onPhone2TypeChange(e.target.value)} className="form-control background-transparent selectable-label" value={phone2value}>
                            {this.renderPhoneContactTypeOptions()}
                        </select>,
                        <div className="flex-container" style={{ alignItems: "center" }}>
                            <div style={{ width: "60%" }}>
                                <TextboxMaskedControl
                                    isValid={this.state.patientModel.get("IsPhone2Valid")}
                                    id={"phone2" + this.id}
                                    onChangeOperation={null}
                                    value={this.state.patientModel.get("Phone2Edit")}
                                    onChangeFunction={val => this.mutatePatientModel(s => s.set("Phone2Edit", val).set("IsPhone2Valid", this.validatePhone(val)))}
                                    mask={phonemask}
                                    validationErrorMessage={phonevalidationErrorMessage}
                                    disabled={this.props.disabled || this.state.createInProgress || this.state.isCheckingEligibility}
                                />
                            </div>
                            <div style={{ width: "calc(40% - 10px)" }}>
                                {showPhone2Ext && (
                                    <div>
                                        <span>{cacheStore.GetString("ExtLabel")}</span>
                                        <div style={{ width: "85px", display: "inline-flex", marginLeft: "5px" }}>
                                            <TextboxMaskedControl
                                                id="phone2Ext"
                                                value={this.state.patientModel.get("Phone2Extension") ?? ""}
                                                onChangeFunction={val => this.mutatePatientModel(s => s.set("Phone2Extension", val))}
                                                mask="[9{1,7}"
                                                isValid
                                                disabled={this.state.isCheckingEligibility}
                                            />
                                        </div>
                                    </div>
                                )}
                                {!showPhone2Ext && showPhone2Notification && (
                                    <CheckboxControl
                                        checked={this.state.patientModel.get("Phone2NotificationsEnabled")}
                                        label={cacheStore.GetString("NotifyPatientLabel")}
                                        labelStyle={{ margin: "0" }}
                                        onChange={checked => this.mutatePatientModel(s => s.set("Phone2NotificationsEnabled", checked))}
                                        disabled={this.state.isCheckingEligibility}
                                    />
                                )}
                            </div>
                        </div>,
                        false,
                        false,
                        "patient-primary-phone-row"
                    )}

                    {/* PHONE 2 */}

                    {this.renderRow(
                        <select style={{ maxWidth: "150px", float: "right" }} onChange={(e: any) => this.onPhone1TypeChange(e.target.value)} className="form-control background-transparent selectable-label" value={phone1value}>
                            {this.renderPhoneContactTypeOptions()}
                        </select>,
                        <div className="flex-container" style={{ alignItems: "center" }}>
                            <div style={{ width: "60%" }}>
                                <TextboxMaskedControl
                                    isValid={this.state.patientModel.get("IsPhone1Valid")}
                                    id={"phone1" + this.id}
                                    value={this.state.patientModel.get("Phone1Edit") != null ? this.state.patientModel.get("Phone1Edit") : ""}
                                    onChangeFunction={val => this.mutatePatientModel(s => s.set("Phone1Edit", val).set("IsPhone1Valid", this.validatePhone(val)))}
                                    mask={phonemask}
                                    validationErrorMessage={phonevalidationErrorMessage}
                                    disabled={this.props.disabled || this.state.createInProgress || this.state.isCheckingEligibility}
                                />
                            </div>

                            <div style={{ width: "calc(40% - 10px)" }}>
                                {showPhone1Ext && (
                                    <div>
                                        <span>{cacheStore.GetString("ExtLabel")}</span>
                                        <div style={{ width: "85px", display: "inline-flex", marginLeft: "5px" }}>
                                            <TextboxMaskedControl
                                                id="phone1Ext"
                                                value={this.state.patientModel.get("Phone1Extension") ?? ""}
                                                onChangeFunction={val => this.mutatePatientModel(s => s.set("Phone1Extension", val))}
                                                mask="[9{1,7}"
                                                validationErrorMessage={phonevalidationErrorMessage}
                                                isValid
                                                disabled={this.state.isCheckingEligibility}
                                            />
                                        </div>
                                    </div>
                                )}
                                {!showPhone1Ext && showPhone1Notification && (
                                    <CheckboxControl
                                        checked={this.state.patientModel.get("Phone1NotificationsEnabled")}
                                        label={cacheStore.GetString("NotifyPatientLabel")}
                                        labelStyle={{ margin: "0" }}
                                        onChange={checked => this.mutatePatientModel(s => s.set("Phone1NotificationsEnabled", checked))}
                                        disabled={this.state.isCheckingEligibility}
                                    />
                                )}
                            </div>
                        </div>
                    )}

                    {/* Email */}

                    {this.renderRow(
                        <select
                            id="EmailType"
                            key="EmailType"
                            className="form-control background-transparent selectable-label"
                            onChange={(e: any) => {
                                const val = e.target.value;
                                this.mutatePatientModel(s => s.set("EmailTypeByLookup", val));
                            }}
                            value={emailTypeValue}
                            disabled={this.state.isCheckingEligibility}
                        >
                            {this.state.emailTypelookups
                                .map((z: FlatLookupView) => {
                                    let longValue = cacheStore.GetLocalizedValueByLookupId(z.LookupID);
                                    return <option key={z.LookupID} value={z.LookupID} title={longValue}> @-  {longValue}</option>;
                                })}
                        </select>,
                        <div className="flex-container" style={{ alignItems: "center" }}>
                            <div style={{ width: "60%" }}>
                                <TextboxMaskedControl
                                    isValid={this.state.patientModel.get("IsEmailValid")}
                                    alphanumericIncludesPeriod={false}
                                    onChangeOperation={null}
                                    id={"Email" + this.id}
                                    dataCy="patientEdit-email-textbox"
                                    value={this.state.patientModel.get("EmailEdit") ?? ""}
                                    onChangeFunction={val => this.mutatePatientModel(s => s.set("EmailEdit", val).set("IsEmailValid", this.validateEmail(val)))}
                                    returnMaskedValue={true}
                                    mask={EmailMask}
                                    // validationErrorMessage = {emailValidationErrorMessage}
                                    disabled={this.props.disabled || this.state.createInProgress || this.state.isCheckingEligibility}
                                    isEmail={true}
                                />
                            </div>
                            <div style={{ width: "calc(40% - 10px)" }}>
                                <CheckboxControl
                                    checked={this.state.patientModel.get("EmailNotificationsEnabled")}
                                    label={cacheStore.GetString("NotifyPatientLabel")}
                                    labelStyle={{ margin: "0" }}
                                    onChange={checked => this.mutatePatientModel(s => s.set("EmailNotificationsEnabled", checked))}
                                    disabled={this.state.isCheckingEligibility}
                                />
                            </div>
                        </div>
                    )}
                    {/* ----------------------------------- */}

                    {this.props.hideFooter == null || !this.props.hideFooter && (
                        <div className="flex-container" style={{ marginTop: "15px", justifyContent: "flex-end" }}>
                            <div><button className="btn btn-default push-right-5" data-cy="patientEdit-cancel-button" onClick={this.onCancelClick.bind(this)} disabled={this.state.createInProgress}> {cacheStore.GetString("CancelLabel")}</button></div>
                            <div><button className="btn btn-primary" data-cy="patientEdit-create-button" disabled={!PatientEditState.IsValid(this.state) || this.state.createInProgress || isDuplicatePopupOpen} onClick={this.onSaveClick.bind(this)}>{cacheStore.GetString("CreateLabel")}</button></div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    private renderRow(label: string | JSX.Element, field: JSX.Element, isRequired?: boolean, showLoading?: boolean, dataCy: string = null): JSX.Element {
        return (
            <div className="row" data-cy={dataCy}>
                <div className="col-xs-4 text-right">
                    {showLoading && (
                        <span className="label-loading">
                            <img style={{ height: "15px", marginTop: "-3px" }} src={img_loading} alt="loading" />
                        </span>
                    )}
                    {isRequired &&
                        <span className="label-error"><strong>*</strong></span>
                    }
                    <span className="form-label">{label}</span>
                </div>
                <div className="col-xs-8">
                    {field}
                </div>
            </div>
        );
    }

    public mutatePatientModel(mutation: (model: IPatientCreationModel) => IPatientCreationModel, then: () => void = null): void {
        this.mutateState(s => s.patientModel = mutation(this.state.patientModel), then);
    }

    public mutateSinglePatientModel(key: any, value: string, then: () => void = null): void {
        this.mutateState(s => s.patientModel = this.state.patientModel.set(key, value), then);
    }

    private onCancelClick(): void {
        sessionStore.dispatch(this.props.cancelOperation);
    }

    private onSaveClick(): void {
        let s = new PatientEditState();
        s.createInProgress = true;
        this.setState(s);
        window.setTimeout(() => {
            sessionStore.dispatch(this.props.saveOperation, { patientEditModel: this.state.patientModel });
        }, 50);
    }

    private onPrefixChanged(e: ChangeEvent<HTMLSelectElement>): void {
        this.mutateSinglePatientModel("SelectedPrefixLookupId", e.target.value);
    }

    private onLastNameChange(val: string): void {
        const nextState = new PatientEditState();
        nextState.patientModel = this.state.patientModel.set("LastName", val);

        this.setState(nextState, () => this.onNAMChange(this.state.patientModel.get("NAMEdit"), false));
    }

    private onFirstNameChange(val: string): void {
        const nextState = new PatientEditState();
        nextState.patientModel = this.state.patientModel.set("FirstName", val);

        this.setState(nextState, () => this.onNAMChange(this.state.patientModel.get("NAMEdit"), false));
    }

    private onPreferredNameChange(val: string): void {
        let s = new PatientEditState();
        s.patientModel = this.state.patientModel.set("PreferredName", val);

        this.setState(s, () => { });
    }

    private onGenderIdentityChange(val: ChangeEvent<HTMLSelectElement>): void {
        const genderIdentity = EnumHelper.getValue(GenderIdentity, val.target.value);
        let s = new PatientEditState();
        s.patientModel = this.state.patientModel.set("GenderIdentity", genderIdentity);

        this.setState(s, () => { });
    }

    private onPreferredPronounsChange(val: ChangeEvent<HTMLSelectElement>): void {
        const preferredPronouns = EnumHelper.getValue(PreferredPronouns, val.target.value);
        let s = new PatientEditState();
        s.patientModel = this.state.patientModel.set("PreferredPronouns", preferredPronouns);

        if (preferredPronouns !== PreferredPronouns.Other) {
            s.patientModel = s.patientModel.set("SpecifiedPreferredPronouns", null);
        }

        this.setState(s, () => { });
    }

    private onSpecifiedPreferredPronounsChange(val: string): void {
        let s = new PatientEditState();
        s.patientModel = this.state.patientModel.set("SpecifiedPreferredPronouns", val);

        this.setState(s, () => { });
    }

    private onDOBChange(val: Date): void {
        const nextState = new PatientEditState();
        nextState.patientModel = this.state.patientModel.set("DOB", val);
        this.setState(nextState, () => this.onNAMChange(this.state.patientModel.get("NAMEdit"), false));
    }

    private onGenderChange(genderLookupId: string): void {
        const nextState = new PatientEditState();
        nextState.patientModel = this.state.patientModel.set("GenderLookupId", genderLookupId);
        this.setState(nextState, () => this.onNAMChange(this.state.patientModel.get("NAMEdit"), false));
    }

    private onCivicAddressSelect(addressControlDto: AddressControlDTO): void {
        let s = new PatientEditState();
        s.patientModel = this.state.patientModel
            .set("CivicAddressEdit", addressControlDto.civicAddressEdit)
            .set("CityEdit", addressControlDto.cityEdit)
            .set("PostalCodeEdit", addressControlDto.postalCodeEdit)
            .set("IsPostalCodeValid", addressControlDto.isPostalCodeValid)
            .set("SelectedCountryLookupId", addressControlDto.selectedCountryLookupId);

        if (!IsNullOrWhiteSpace(addressControlDto.selectedRegionLookupId)) {
            s.patientModel = s.patientModel
                .set("SelectedRegionLookupId", addressControlDto.selectedRegionLookupId);
        }

        this.setState(s);
    }

    /**
     * @param rawNAM        raw NAM value.
     * @param isDirectModification set to true if event source comes directly from NAM control, false if this is called from other inputs that have an impact on NAM (ie firstname,lastname,dob)
     *                             this is to deal with triggering duplicateCheck validation after it has been dismissed ('continue with creation' clicked). If it is shown and dismissed,
     *                             we do not want to show it again unless the nam has been directly edited again.
     */
    private onNAMChange(rawNAM: string, isDirectModification: boolean = true): void {
        clearTimeout(this.ontarioEligibilityCheckTimeoutId);
        const healthCardRegionLookupId = IJPatientCreationModel.getHealthCardRegionLookupId(this.state.patientModel);

        const namHasChanged = this.state.patientModel.get("NAMEdit") != rawNAM;
        const s = new PatientEditState();
        s.patientModel = this.state.patientModel;

        const resolvedBirthDate = PatientHelper.getDateOfBirthFromNAM(rawNAM, healthCardRegionLookupId);

        if (resolvedBirthDate != null && !s.patientModel.get("DOB"))
            s.patientModel = s.patientModel.set("DOB", resolvedBirthDate);

        s.patientModel = s.patientModel.set("NAMEdit", rawNAM);
        if (isDirectModification || !s.patientModel.get("GenderLookupId"))
            s.patientModel = this.setGenderFromNAM(rawNAM, s.patientModel);

        s.patientModel = s.patientModel.set("IsNAMValid", this.isHealthCardValid(rawNAM, healthCardRegionLookupId));

        if (!rawNAM) {
            s.patientModel = s.patientModel.set("NAMExpiryEdit", null);
            s.patientModel = s.patientModel.set("IsNAMValid", true);
        }

        if (s.patientModel.get("IsNAMValid") === true && s.patientModel.get("NAMEdit") && !this.props.shouldNotValidateDuplicate) {
            this.validateIsNotDuplicate(
                new FindPatientDuplicateRequest({
                    number: s.patientModel.get("NAMEdit"),
                    regionLookupId: healthCardRegionLookupId
                })
            );
        }

        this.setState(
            s,
            async () => {
                const namIsValid = s.patientModel.get("IsNAMValid");
                if (namIsValid) {
                    this.props.onValidHealthCardInfo?.(s.patientModel);

                    if (
                        cacheStore.GetLookupIdForProvince(CanadaState.Ontario) === healthCardRegionLookupId
                        && cacheStore.ClinicSettings.EnableOntarioHealthInsurancePlanEligibility
                        && rawNAM
                        && isDirectModification
                        && namHasChanged
                    )
                        await this.validateOntarioHealthPlanEligibilityAsync(rawNAM);
                }
            }
        );
    }

    private handleOnHealthCardRegionChange(regionLookupId: string): void {
        let s = new PatientEditState();
        s.patientModel = this.state.patientModel;

        s.patientModel = s.patientModel
            .set("selectedHealthCardRegionLookupId", regionLookupId)
            .set("NAMEdit", "");

        s.patientModel.set("IsNAMValid", this.isHealthCardValid(s.patientModel.get("NAMEdit"), regionLookupId));

        if (s.patientModel.get("IsNAMValid") === true && s.patientModel.get("NAMEdit") && !this.props.shouldNotValidateDuplicate) {
            this.validateIsNotDuplicate(
                new FindPatientDuplicateRequest({
                    number: s.patientModel.get("NAMEdit"),
                    regionLookupId: IJPatientCreationModel.getHealthCardRegionLookupId(s.patientModel)
                })
            );
        }

        this.setState(s, () => s.patientModel.get("IsNAMValid") && this.props.onValidHealthCardInfo?.(s.patientModel));
    }

    private renderPhoneContactTypeOptions(): JSX.Element[] {
        return this.state.phoneTypeLookups.map((phonetypeLookup: [FlatLookupView, FlatLookupView]) => {
            const contactType = phonetypeLookup[0];
            const phoneType = phonetypeLookup[1];

            const localizedContactType = cacheStore.GetLocalizedValueByLookupId(contactType.LookupID);
            const localizedPhoneType = cacheStore.GetLocalizedValueByLookupId(phoneType.LookupID);

            const id = `${contactType.LookupID}_${phoneType.LookupID}`;
            const shortValue = `${localizedContactType.substr(0, 1)} - ${localizedPhoneType}`;
            const longValue = `${localizedContactType} - ${localizedPhoneType}`;

            return <option key={id} value={id} title={longValue}>{shortValue}</option>;
        });
    }

    private isHealthCardValid(healthcard: string, regionLookupId: string): boolean {
        let patientModel = this.state.patientModel;

        if (!cacheStore.ClinicSettings.EnableHealthCardValidation) {
            const isOntarioHealthCard = regionLookupId == cacheStore.FindLookupByLookupCode("CanadaState_ON").LookupID;
            if (isOntarioHealthCard)
                return true;

            return this.validationHelper.validateNAM(
                healthcard,
                patientModel.get("SelectedCountryLookupId"),
                regionLookupId,
                patientModel.get("FirstName"),
                patientModel.get("LastName"),
                patientModel.get("DOB"),
                patientModel.get("GenderLookupId")
            );
        }

        const validator = HealthCardValidatorFactory.getValidator(cacheStore.GetDefaultCountryForActiveSite().LookupID, regionLookupId);
        const request = new HealthCardValidatorRequest(
            healthcard?.trim() || "",
            patientModel.get("FirstName"),
            patientModel.get("LastName"),
            patientModel.get("DOB"),
            patientModel.get("GenderLookupId")
        );

        return validator.isValid(request);
    }

    private onNAMExpiryChange(val: Date): void {
        let s = new PatientEditState();
        s.patientModel = this.state.patientModel.set("NAMExpiryEdit", val);
        this.setState(s);
    }

    private setGenderFromNAM(val: string, currentModel: IPatientCreationModel): IPatientCreationModel {
        // QUEBEC NAM should have last word on gender of patient. if a patient changed gender.. patient needs to get a new RAMQ number
        if (val?.length >= 7 && IJPatientCreationModel.getHealthCardRegionLookupId(currentModel) == cacheStore.FindLookupByLookupCode("CanadaState_QC").LookupID) {
            let monthFirstDigit = parseInt(val.substr(6, 1));

            if (monthFirstDigit > 1)
                currentModel = currentModel.set("GenderLookupId", this.femaleLookupId.LookupID);
            else
                currentModel = currentModel.set("GenderLookupId", this.maleLookupId.LookupID);

            if (IsNullOrWhiteSpace(currentModel.get("SelectedPrefixLookupId")))
                currentModel = currentModel.set("SelectedPrefixLookupId", monthFirstDigit > 1 ? this.femalePrefixLookupId.LookupID : this.malePrefixLookupId.LookupID);
        }

        return currentModel;
    }

    private validatePhone(val: string): boolean {
        if (val == null || val.length == 0)
            return true;

        // fetch phoneNUmber validation info
        let phonevalidationRule = this.validationHelper.getPhoneNumberValidationRule(this.state.patientModel.get("SelectedCountryLookupId"), null); // no need for region when checking for phone number format?
        let phonevalidationRegex: string = phonevalidationRule ? phonevalidationRule.ValidationRegexPattern : "";
        let isValid = true;

        if (phonevalidationRegex && phonevalidationRegex.length > 0) {
            let regexp = new RegExp(phonevalidationRegex, "i");
            isValid = ValidationHelper.isExactMatch(val, regexp);
        }

        return isValid;
    }

    private validateEmail(val: string): boolean {
        if (val == null || val.length == 0)
            return true;
        // fetch email validation info
        let isValid = true;
        isValid = ValidationHelper.isExactMatch(val, ValidationHelper.emailRegex);

        return isValid;
    }

    // ------------------------------------------------------------------------------------------------
    // - PHONE  [ pl says to future pl : we need to eventually refactor phone edit stuff in a single reusable component ]
    // ------------------------------------------------------------------------------------------------
    private onPhone1TypeChange(val: string): void {
        this.mutatePatientModel(s => {
            let contactPointLookupId = val.split("_")[0];
            let phoneLookupId = val.split("_")[1];

            s = s.set("Phone1TypeLookupId", phoneLookupId)
                .set("Phone1ContactTypeLookupId", contactPointLookupId);
            if (phoneLookupId !== this.cellPhoneLookupId)
                s = s.set("Phone1NotificationsEnabled", false);
            return s;
        });
    }

    private onPhone2TypeChange(val: string): void {
        this.mutatePatientModel(s => {
            let contactPointLookupId = val.split("_")[0];
            let phoneLookupId = val.split("_")[1];

            s = s.set("Phone2TypeLookupId", phoneLookupId)
                .set("Phone2ContactTypeLookupId", contactPointLookupId);
            if (phoneLookupId !== this.cellPhoneLookupId)
                s = s.set("Phone2NotificationsEnabled", false);
            return s;
        });
    }

    // ------------------------------------------------------------------------------------------------
    // - VALIDATE "NOT DUPLICATE PATIENT" LOGIC -------------------------------------------------------
    // ------------------------------------------------------------------------------------------------
    private renderDuplicatePopup(): JSX.Element {
        let result: JSX.Element = null;
        let duplicateContactIds = this.state.patientModel.get("DuplicatePatientContactIds")?.toArray();
        let cssClass = this.props.duplicatePopupCSS != null ? "sb-reg-popup " + this.props.duplicatePopupCSS : "sb-reg-popup";

        result = (
            <div className={cssClass}>
                <div className="duplicate-patient-content">
                    <h3>{cacheStore.GetString("DuplicateTitleLabel")}</h3>

                    <strong>{cacheStore.GetString("DuplicateSentence1Label")}</strong>
                    <div className="margin-sm-bottom">
                        {cacheStore.GetString(this.props.duplicatePromptLabelName)}
                    </div>

                    <div className="patient-sidebar-search-container full-size">
                        <IndividualAdvancedSearchControl
                            defaultTextValue="xxx"
                            isFocusOnLoad
                            componentId="_patientAdvancedSearchDuplicate"
                            maxResultsToReturn={8}
                            onIndividualSelectCallback={person => this.onDuplicateSelection(person)}
                            specificContactIdsToDisplay={duplicateContactIds}
                        />
                    </div>
                </div>
                <div className="p-footer text-right padding-md-right padding-sm-bottom">
                    <button
                        className="btn btn-default"
                        onClick={() => this.onDuplicateCancelClick()}
                        disabled={this.props.disabled || this.state.createInProgress || this.state.isCheckingEligibility}
                    >
                        {cacheStore.GetString("BackLabel")}
                    </button>
                    {this.props.enableQuickPatientConversionForDuplicates && this.state.duplicatePerson?.ContactFacetType === ContactFacetType.Relative && (
                        <button
                            className="btn btn-primary margin-sm-left"
                            onClick={() => this.onConvertDuplicateRelative()}
                            disabled={this.props.disabled || this.state.createInProgress || this.state.isCheckingEligibility}
                        >
                            {cacheStore.GetString("CreatePatientFileLabel")}
                        </button>
                    )}
                </div>
            </div>
        );

        return result;
    }

    private onDuplicateCancelClick(): void {
        let s = new PatientEditState();
        s.patientModel = this.state.patientModel.set("DuplicatePopupOpen", false);
        s.duplicatePerson = null;
        this.setState(s);
    }

    private onDuplicateSelection(person: Person): void {
        let s = new PatientEditState();
        s.duplicatePerson = person;

        if (!this.props.enableQuickPatientConversionForDuplicates || person.ContactFacetType !== ContactFacetType.Relative) {
            s.patientModel = this.state.patientModel.set("DuplicatePopupOpen", false);

            if (this.props.duplicateContinueOperation) {
                sessionStore.dispatch(
                    this.props.duplicateContinueOperation,
                    { person: person }
                );
            }

            this.props.duplicateContinueCallback?.(person);
        }

        this.setState(s);
    }

    private async onConvertDuplicateRelative(): Promise<void> {
        const requestData = {
            ContactFacetId: this.state.duplicatePerson.ContactFacetId
        };

        const response = await Client.postAsync<any>(ServerOperations.Server.Myle_ConvertRelativeToPatient, requestData);

        if (!response.isSuccess) {
            return;
        }

        sessionStore.dispatch(
            LocalOperations.Person.CloseFile,
            { contactId: this.state.duplicatePerson.ContactId, redirect: false },
            0
        );

        this.state.duplicatePerson.ContactFacetType = ContactFacetType.Relative;

        if (this.props.duplicateContinueOperation) {
            sessionStore.dispatch(
                this.props.duplicateContinueOperation,
                { person: this.state.duplicatePerson }
            );
        }

        this.props.duplicateContinueCallback?.(this.state.duplicatePerson);
    }

    private async validateIsNotDuplicate(healthCardInfo: FindPatientDuplicateRequest): Promise<void> {
        if (this.alreadyDuplicateCheckedNam?.equals(healthCardInfo))
            return;

        this.alreadyDuplicateCheckedNam = healthCardInfo;

        const duplicateIndividualsIds = await IndividualApiClient.getDuplicatesAsync(healthCardInfo.number, healthCardInfo.regionLookupId);

        const s = new PatientEditState();
        s.patientModel = this.state.patientModel;

        let followUp;

        if (!isArrayNullOrUndefinedOrEmpty(duplicateIndividualsIds) && healthCardInfo.number) {
            s.patientModel = s.patientModel.set("DuplicatePatientContactIds", Immutable.List(duplicateIndividualsIds.map(p => p.Id)));

            if (!s.patientModel.get("DuplicatePopupOpen"))
                s.patientModel = s.patientModel.set("DuplicatePopupOpen", true);
        }
        else {
            if (this.state.patientModel.get("SelectedRegionLookupId") == cacheStore.FindLookupByLookupCode("CanadaState_QC").LookupID)
                followUp = this.getPatientNameAtRamq(healthCardInfo.number);

            s.patientModel = s.patientModel.set("DuplicatePatientContactIds", Immutable.List([]));
        }

        if (followUp != null)
            this.setState(s, followUp);
        else
            this.setState(s);
    }

    private getPatientNameAtRamq(nam: string): void {
        let self = this;

        if (self.alreadyResolvedNam == nam)
            return; // Already resolve

        self.alreadyResolvedNam = nam;

        let requestData = {
            Patient: { NAM: nam },
            CurrentUser: sessionStore.getCurrentEmployee().getRamqCredentials(),
            ForceB2BPatientNameResolve: true
        };

        let s = new PatientEditState();
        s.isLoadingRAMQ = true;
        self.setState(s, () => {
            Client.Post(
                ServerOperations.Server.RAMQ_CheckSubscription,
                requestData,
                response => {
                    let ramqInfo: RAMQInfo = response;

                    let s = new PatientEditState();
                    s.patientModel = this.state.patientModel;
                    if (!IsNullOrWhiteSpace(ramqInfo.FirstName) || !IsNullOrWhiteSpace(ramqInfo.LastName)) {
                        if (IsNullOrWhiteSpace(self.state.patientModel.get("LastName")) && !IsNullOrWhiteSpace(ramqInfo.LastName)) {
                            s.patientModel = s.patientModel.set("LastName", ramqInfo.LastName);
                        }

                        if (IsNullOrWhiteSpace(self.state.patientModel.get("FirstName")) && !IsNullOrWhiteSpace(ramqInfo.LastName)) {
                            s.patientModel = s.patientModel.set("FirstName", ramqInfo.FirstName);
                        }
                    }
                    s.isLoadingRAMQ = false;
                    self.setState(s);
                },
                () => {
                    let s = new PatientEditState();
                    s.isLoadingRAMQ = false;
                    self.setState(s);
                }
            );
        });
    }

    private async validateOntarioHealthPlanEligibilityAsync(healthCardNumber: string): Promise<void> {
        const action = (): void => {
            this.mutateState(state => state.isCheckingEligibility = true, async () => {
                const patientHealthCardProvinceLookupId = cacheStore.GetLookupIdForProvince(CanadaState.Ontario);
                const eligibility = await HealthInsuranceEligibilityClient.getHealthInsuranceEligibility(healthCardNumber, null, patientHealthCardProvinceLookupId);
                const nextState = new PatientEditState(this.state);
                nextState.isCheckingEligibility = false;

                if (!eligibility)
                    sessionStore.showPopup("Error while fetching the eligibility info");
                else if (eligibility.PatientDemographicInformation)
                    nextState.patientModel = this.state.patientModel
                        .set("FirstName", eligibility.PatientDemographicInformation.FirstName)
                        .set("LastName", eligibility.PatientDemographicInformation.LastName)
                        .set("DOB", eligibility.PatientDemographicInformation.DateOfBirth)
                        .set("NAMExpiryEdit", eligibility.EligibilityExpiryDate)
                        .set("GenderLookupId", MyleHelper.enumGenderToGenderLookupId(eligibility.PatientDemographicInformation.Gender));

                this.setState(nextState);
            });
        };

        this.ontarioEligibilityCheckTimeoutId = setTimeout(action, 750);
    }
}

export { PatientEdit, PatientEditState };
